import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, exhaustMap, map } from 'rxjs/operators';

import { ToastService } from '../../../../modules/shared/services/toast-message.service';
import { AssetsService } from '../../../../services/digital-footprint/assets/assets.service';
import { PagedResponse } from '../../general/models/general';
import * as actions from './assets.actions';
import {
  Asset,
  IAssetIpHistory,
  IAssetRemediation,
  IAssetsDomainDetails,
  IAssetVulnerabilities,
} from './models/Asset';

@Injectable()
export class AssetsEffects {
  private actions$ = inject(Actions);
  private service = inject(AssetsService);
  private toastService = inject(ToastService);

  getDigitalFootprintAssets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getAssets),
      exhaustMap(({ sortOptions, searchTerm, filters, pageNumber, query }) =>
        this.service
          .getAssets({ ...sortOptions, searchTerm, filters, pageNumber, query })
          .pipe(
            map((pagedAssets: PagedResponse<Asset>) => {
              return actions.getAssetsSuccess({ pagedAssets });
            }),
            catchError((error: any) => of(actions.getAssetsFailure(error))),
          ),
      ),
    ),
  );

  getDFAssetsVulnerabilities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getAssetVulnerabilities),
      exhaustMap(({ assetId }) =>
        this.service.getAssetVulnerabilities(assetId).pipe(
          map(
            (
              pagedAssetVulnerabilities: PagedResponse<IAssetVulnerabilities>,
            ) => {
              return actions.getAssetsVulnerabilitiesSuccess({
                pagedAssetVulnerabilities,
              });
            },
          ),
          catchError((error: any) =>
            of(actions.getAssetsVulnerabilitiesFailure(error)),
          ),
        ),
      ),
    ),
  );

  getAssetsGraph$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getAssetsGraph),
      exhaustMap(() =>
        this.service.getAssetsGraph().pipe(
          map((assetsGraph) => {
            return actions.getAssetsGraphSuccess({
              assetsGraph,
            });
          }),
          catchError((error: any) => of(actions.getAssetsGraphSuccess(error))),
        ),
      ),
    ),
  );

  getAssetsGraphDomainSubdomain$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getAssetsGraphDomainSubdomain),
      exhaustMap(({ id }) =>
        this.service.getAssetsGraphDomainSubdomainOverview(id).pipe(
          map((assetsGraphDomainSubdomain) => {
            return actions.getAssetsGraphDomainSubdomainSuccess({
              assetsGraphDomainSubdomain,
            });
          }),
          catchError((error: any) =>
            of(actions.getAssetsGraphDomainSubdomainFailure(error)),
          ),
        ),
      ),
    ),
  );

  getAssetsGraphOpenPort$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getAssetsGraphOpenPort),
      exhaustMap(({ id }) =>
        this.service.getAssetsGraphOpenPortOverview(id).pipe(
          map((assetsGraphOpenPort) => {
            return actions.getAssetsGraphOpenPortSuccess({
              assetsGraphOpenPort,
            });
          }),
          catchError((error: any) =>
            of(actions.getAssetsGraphOpenPortFailure(error)),
          ),
        ),
      ),
    ),
  );

  getAssetsIpAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getAssetsGraphIpAddress),
      exhaustMap(({ id }) =>
        this.service.getAssetsIpAddressOverview(id).pipe(
          map((assetsGraphIpAddress) => {
            return actions.getAssetsGraphIpAddressSuccess({
              assetsGraphIpAddress,
            });
          }),
          catchError((error: any) =>
            of(actions.getAssetsGraphIpAddressFailure(error)),
          ),
        ),
      ),
    ),
  );

  setPrimaryDomain$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setAssetsPrimaryDomain),
      exhaustMap(({ id }) =>
        this.service.setPrimaryDomain(id).pipe(
          map(() => {
            return actions.setAssetsPrimaryDomainSuccess();
          }),
          catchError((error: any) =>
            of(actions.setAssetsPrimaryDomainFailure(error)),
          ),
        ),
      ),
    ),
  );

  setPrimaryDomainSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setAssetsPrimaryDomainSuccess),
      map(() => {
        this.toastService.success('Primary domain updated.');
        return actions.getAssetsGraph();
      }),
    ),
  );

  // ---- asset detail page ----
  getAssetOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getAssetOverview),
      exhaustMap(({ id }) =>
        this.service.getAssetOverview(id).pipe(
          map((assetOverview: IAssetsDomainDetails) =>
            actions.getAssetOverviewSuccess({ assetOverview }),
          ),
          catchError((error: any) =>
            of(actions.getAssetOverviewFailure(error)),
          ),
        ),
      ),
    ),
  );

  getAssetIpHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getAssetIpHistory),
      exhaustMap(({ id }) =>
        this.service.getAssetIpHistory(id).pipe(
          map((assetIpHistory: IAssetIpHistory) =>
            actions.getAssetIpHistorySuccess({ assetIpHistory }),
          ),
          catchError((error: any) =>
            of(actions.getAssetIpHistoryFailure(error)),
          ),
        ),
      ),
    ),
  );


  getAssetSubdomains$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getAssetSubdomains),
      exhaustMap(({ id }) =>
        this.service.getAssetSubdomains(id).pipe(
          map((paged: PagedResponse<Asset>) =>
            actions.getAssetSubdomainsSuccess({
              assetSubdomains: paged?.items ?? [],
            }),
          ),
          catchError((error: any) =>
            of(actions.getAssetSubdomainsFailure(error)),
          ),
        ),
      ),
    ),
  );

  getAssetRemediation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getAssetRemediation),
      exhaustMap(({ id }) =>
        this.service.getAssetRemediation(id).pipe(
          map((assetRemediation: IAssetRemediation) =>
            actions.getAssetRemediationSuccess({ assetRemediation }),
          ),
          catchError((error: any) =>
            of(actions.getAssetRemediationFailure(error)),
          ),
        ),
      ),
    ),
  );
}
